// A reusable horizontal bar chart (plain HTML/CSS, no charting library) —
// handles both a single-series ordinal ramp (one color per category, order
// carries meaning) and a two-series comparison (a fixed pair of colors +
// legend). Mark spec: bars <=24px thick, rounded at the data-end/square at
// the baseline, a 2px gap between a category's stacked series, a direct
// value label at every bar's tip, category labels in muted ink (never
// colored text) — see the dataviz skill's marks-and-anatomy reference.
export interface BarChartDatum {
  label: string;
  values: number[];
}

interface BarChartProps {
  data: BarChartDatum[];
  seriesColors: string[];
  seriesLabels?: string[];
  formatValue?: (n: number) => string;
}

export function BarChart({ data, seriesColors, seriesLabels, formatValue = (n) => String(n) }: BarChartProps) {
  const max = Math.max(1, ...data.flatMap((d) => d.values));

  return (
    <div className="bar-chart">
      {seriesLabels && (
        <div className="bar-chart-legend">
          {seriesLabels.map((label, i) => (
            <span className="bar-chart-legend-item" key={label}>
              <span className="bar-chart-swatch" style={{ background: seriesColors[i] }} />
              {label}
            </span>
          ))}
        </div>
      )}
      <div className="bar-chart-rows">
        {data.map((d) => (
          <div className="bar-chart-row" key={d.label}>
            <span className="bar-chart-label">{d.label}</span>
            <div className="bar-chart-bars">
              {d.values.map((value, i) => (
                <div className="bar-chart-track" key={i}>
                  <div
                    className="bar-chart-bar"
                    style={{ width: `${Math.max((value / max) * 100, value > 0 ? 1.5 : 0)}%`, background: seriesColors[i] }}
                    title={seriesLabels ? `${seriesLabels[i]}: ${formatValue(value)}` : formatValue(value)}
                  />
                  <span className="bar-chart-value">{formatValue(value)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
